import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Save, Loader2, GripVertical, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PROCESS_TYPES = [
  { slug: 'admissao', nome: 'Admissão' },
  { slug: 'desligamento', nome: 'Desligamento' },
];

const CONTRACT_TYPES = [
  { slug: 'clt', nome: 'CLT' },
  { slug: 'pj', nome: 'PJ' },
  { slug: 'ambos', nome: 'Ambos' },
];

const RHConfigProcessesPage = () => {
  const [processType, setProcessType] = useState('admissao');
  const [contractType, setContractType] = useState('clt');
  const [availableFields, setAvailableFields] = useState([]);
  const [availableDocs, setAvailableDocs] = useState([]);
  const [activeFields, setActiveFields] = useState([]);
  const [activeDocs, setActiveDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadConfig();
  }, [processType, contractType]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [fieldsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/rh/available-fields`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/rh/available-documents`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAvailableFields(fieldsRes.data);
      setAvailableDocs(docsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const [fieldsRes, docsRes] = await Promise.all([
        axios.get(`${API}/api/rh/process-fields`, {
          params: { process_type_slug: processType, contract_type: contractType },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/api/rh/required-documents`, {
          params: { process_type_slug: processType, contract_type: contractType },
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setActiveFields(fieldsRes.data);
      setActiveDocs(docsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar configuração');
    }
  };

  const isFieldActive = (fieldKey) => activeFields.some(f => f.field_key === fieldKey);
  const isDocActive = (docNome) => activeDocs.some(d => d.nome === docNome);

  const getActiveField = (fieldKey) => activeFields.find(f => f.field_key === fieldKey);
  const getActiveDoc = (docNome) => activeDocs.find(d => d.nome === docNome);

  const toggleField = async (availField, active) => {
    try {
      const token = localStorage.getItem('token');
      if (active) {
        await axios.post(`${API}/api/rh/process-fields`, {
          process_type_slug: processType,
          contract_type: contractType,
          field_key: availField.field_key,
          field_label: availField.field_label,
          field_type: availField.field_type,
          required: false,
          order: activeFields.length + 1,
          section: availField.section || 'Geral',
          sync_to_employee: availField.sync_to_employee || false,
          options_json: availField.options || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const field = getActiveField(availField.field_key);
        if (field) {
          await axios.delete(`${API}/api/rh/process-fields/${field.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar campo');
    }
  };

  const toggleDoc = async (availDoc, active) => {
    try {
      const token = localStorage.getItem('token');
      if (active) {
        await axios.post(`${API}/api/rh/required-documents`, {
          process_type_slug: processType,
          contract_type: contractType,
          nome: availDoc.nome,
          descricao: availDoc.descricao,
          obrigatorio: true,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const doc = getActiveDoc(availDoc.nome);
        if (doc) {
          await axios.delete(`${API}/api/rh/required-documents/${doc.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar documento');
    }
  };

  const updateField = async (fieldId, data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rh/process-fields/${fieldId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar campo');
    }
  };

  const updateDoc = async (docId, data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rh/required-documents/${docId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadConfig();
    } catch (error) {
      toast.error('Erro ao atualizar documento');
    }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  const sections = [...new Set(availableFields.map(f => f.section).filter(Boolean))];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Parametrização de Processos RH</h1>
        <p className="text-sm text-gray-500">Configure campos e documentos obrigatórios para cada tipo de processo</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="space-y-1">
          <Label>Tipo de Processo</Label>
          <Select value={processType} onValueChange={setProcessType}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROCESS_TYPES.map(t => <SelectItem key={t.slug} value={t.slug}>{t.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Regime</Label>
          <Select value={contractType} onValueChange={setContractType}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTRACT_TYPES.map(t => <SelectItem key={t.slug} value={t.slug}>{t.nome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="fields">
        <TabsList>
          <TabsTrigger value="fields">
            Campos do Formulário ({activeFields.length}/{availableFields.length})
          </TabsTrigger>
          <TabsTrigger value="documents">
            Documentos ({activeDocs.length}/{availableDocs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="fields" className="mt-4 space-y-4">
          {sections.map(section => {
            const sectionFields = availableFields.filter(f => f.section === section);
            return (
              <Card key={section}>
                <CardHeader><CardTitle className="text-base">{section}</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">Ativo</TableHead>
                        <TableHead>Campo</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="w-24">Obrigatório</TableHead>
                        <TableHead className="w-28">Salvar no Func.</TableHead>
                        <TableHead className="w-24">Ordem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sectionFields.map(af => {
                        const active = isFieldActive(af.field_key);
                        const act = getActiveField(af.field_key);
                        return (
                          <TableRow key={af.field_key} className={active ? '' : 'opacity-50'}>
                            <TableCell>
                              <Switch checked={active} onCheckedChange={(v) => toggleField(af, v)} />
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{af.field_label}</span>
                              <span className="text-xs text-gray-500 ml-2">{af.field_key}</span>
                            </TableCell>
                            <TableCell><Badge variant="outline">{af.field_type}</Badge></TableCell>
                            <TableCell>
                              {active ? (
                                <Switch
                                  checked={act?.required || false}
                                  onCheckedChange={(v) => updateField(act.id, { required: v })}
                                />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {af.sync_to_employee ? (
                                <Badge className="bg-blue-100 text-blue-800">Sim</Badge>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {active ? (
                                <Input
                                  type="number"
                                  className="w-16 h-8 text-sm"
                                  value={act?.order || 0}
                                  onChange={(e) => updateField(act.id, { order: parseInt(e.target.value) || 0 })}
                                />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Ativo</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="w-28">Obrigatório</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {availableDocs.map(ad => {
                    const active = isDocActive(ad.nome);
                    const act = getActiveDoc(ad.nome);
                    return (
                      <TableRow key={ad.nome} className={active ? '' : 'opacity-50'}>
                        <TableCell>
                          <Switch checked={active} onCheckedChange={(v) => toggleDoc(ad, v)} />
                        </TableCell>
                        <TableCell><span className="font-medium">{ad.nome}</span></TableCell>
                        <TableCell><span className="text-sm text-gray-500">{ad.descricao}</span></TableCell>
                        <TableCell>
                          {active ? (
                            <Switch
                              checked={act?.obrigatorio || false}
                              onCheckedChange={(v) => updateDoc(act.id, { obrigatorio: v })}
                            />
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RHConfigProcessesPage;
